import React, { useState, useRef, useEffect } from "react";
import { Toast } from 'primereact/toast';
import { couponsAPI } from "../../services/couponService";
import CouponForm from "./CouponForm";
import CouponTable from "./CouponTable";

export default function CouponGenerator() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [allCoupons, setAllCoupons] = useState([]);
  const [previousCoupons, setPreviousCoupons] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showAllCoupons, setShowAllCoupons] = useState(false);
  const [hasGeneratedCoupons, setHasGeneratedCoupons] = useState(false);

  const [feeType, setFeeType] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [count, setCount] = useState("");

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 5,
  });

  const feeTypeOptions = [
    { label: 'Tuition Fee', value: 'Tuition Fee' },
    { label: 'Transport Fee', value: 'Transport Fee' },
    { label: 'Library Fee', value: 'Library Fee' },
    { label: 'Exam Fee', value: 'Exam Fee' }
  ];

  const calculateRows = () => {
    const availableHeight = window.innerHeight - 300;
    if (availableHeight > 800) return 8;
    if (availableHeight > 600) return 6;
    return 5;
  };

  useEffect(() => {
    const handleResize = () => {
      setLazyParams((p) => ({ ...p, rows: calculateRows() }));
    };

    window.addEventListener('resize', handleResize);
    setLazyParams((p) => ({ ...p, rows: calculateRows() }));

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAllCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllCoupons = async () => {
    setLoading(true);
    try {
      const res = await couponsAPI.getAllCoupons();
      const coupons = res?.data?.coupons || [];

      setAllCoupons(coupons);
      setPreviousCoupons(coupons);
      setTotalRecords(coupons.length);
    } catch (err) {
      console.error('Failed to load coupons', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load coupons' });
    } finally {
      setLoading(false);
    }
  };

  const generateCoupons = async () => {
    if (!feeType || !discountPct || !count) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please fill all fields' });
      return;
    }

    try {
      const res = await couponsAPI.generateMultiple(feeType, discountPct, count);
      const couponsArray = res?.data?.coupons || [];

      const mapped = couponsArray.map((item) => {
        let codeValue = '';
        if (typeof item === 'string') codeValue = item;
        else if (item && typeof item === 'object') codeValue = item.code || item.coupon || item.code_value || JSON.stringify(item);
        else codeValue = String(item);
        return { code: codeValue, feeType, discountPct, createdAt: new Date().toISOString() };
      });

      setPreviousCoupons(mapped);
      setAllCoupons((prev) => [...mapped, ...prev]);
      setTotalRecords((prev) => prev + mapped.length);
      setShowAllCoupons(false);
      setHasGeneratedCoupons(true);
      setLazyParams((p) => ({ ...p, first: 0 }));

      toast.current?.show({ severity: 'success', summary: 'Success', detail: res?.data?.message || `${count} coupons generated` });

      setFeeType("");
      setDiscountPct("");
      setCount("");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.error || err?.message || 'Something went wrong';
      toast.current?.show({ severity: 'error', summary: 'Error', detail });
    }
  };

  const displayCoupons = showAllCoupons ? allCoupons : previousCoupons;
  const displayTotal = showAllCoupons ? totalRecords : previousCoupons.length;

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4 bg-gray-50">
      <Toast ref={toast} />

      <CouponForm 
        feeType={feeType}
        setFeeType={setFeeType}
        discountPct={discountPct}
        setDiscountPct={setDiscountPct}
        count={count}
        setCount={setCount}
        generateCoupons={generateCoupons}
        feeTypeOptions={feeTypeOptions}
      />

      <CouponTable 
        displayCoupons={displayCoupons}
        displayTotal={displayTotal}
        loading={loading}
        lazyParams={lazyParams}
        setLazyParams={setLazyParams}
        showAllCoupons={showAllCoupons}
        setShowAllCoupons={setShowAllCoupons}
        hasGeneratedCoupons={hasGeneratedCoupons}
      />
    </div>
  );
}
